import Endpoint from "@mongez/http";
import { GenericObject } from "@mongez/reinforcements";
import { useLocation } from "@remix-run/react";
import { useState } from "react";

type FetchOptions<Data> = {
  params?: GenericObject;
  onSuccess?: (data: Data) => void;
  onError?: (error: Error) => void;
  updateState?: boolean;
};

export function useFetch<Data>(
  path?: string,
  options: FetchOptions<Data> = {},
) {
  const [data, setData] = useState<Data>();
  const [error, setError] = useState<Error | null>(null);
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  const load = async (params?: GenericObject) => {
    const updateState = options.updateState ?? true;

    if (updateState) {
      setIsLoading(true);
      setError(null);
    }

    try {
      const endpoint = new Endpoint();

      const response = await endpoint.get<Data>(path || location.pathname, {
        params: {
          ...params,
          ...options.params,
        },
      });

      options.onSuccess?.(response.data);

      if (updateState) {
        setData(response.data);
        setIsLoading(false);
      }

      // if (response.data.user) {
      //   userAtom.update(response.data.user);
      // }

      return response.data;
    } catch (err: any) {
      const error =
        err instanceof Error
          ? err
          : new Error(err?.message || "An error occurred");

      if (updateState) {
        setError(error);
        setIsLoading(false);
      }

      // Log error for debugging
      console.error("useFetch error:", {
        path: path || location.pathname,
        params: { ...params, ...options.params },
        error: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });

      options.onError?.(error);
      throw error;
    }
  };

  const reset = () => {
    setData(undefined);
    setError(null);
  };

  return { load, data, error, reset, isLoading };
}
